import { computed, onScopeDispose, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useQueryClient } from '@tanstack/vue-query'
import { getConfig } from '@shared/config/runtime'
import { setI18nLocale } from '@shared/i18n'
import { sessionApi } from '@shared/api/session.api'
import { useAuthStore } from '@shared/stores/auth'
import { useUiStore } from '@shared/stores/ui'
import { useNotificationStore } from '@shared/stores/notifications'
import { useIdleLogout } from '@shared/composables/useIdleLogout'
import { setObservabilityContext } from '@shared/observability'

/** App-wide side effects: theme, locale, document title, idle logout, unread count. */
export function useAppShell() {
  const ui = useUiStore()
  const auth = useAuthStore()
  const notifications = useNotificationStore()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { t } = useI18n()

  // Theme → data-theme on <html>
  const media = window.matchMedia('(prefers-color-scheme: dark)')
  const applyTheme = () => {
    const dark = ui.theme === 'dark' || (ui.theme === 'system' && media.matches)
    document.documentElement.dataset.theme = dark ? 'dark' : 'light'
  }
  watch(() => ui.theme, applyTheme, { immediate: true })
  media.addEventListener('change', applyTheme)
  onScopeDispose(() => media.removeEventListener('change', applyTheme))

  // Locale → lazy-load messages, refetch server-translated content
  watch(
    () => ui.locale,
    async (l, prev) => {
      await setI18nLocale(l)
      if (prev) await queryClient.invalidateQueries()
    },
    { immediate: true },
  )

  // Document title from route meta
  watch(
    () => [router.currentRoute.value, ui.locale] as const,
    ([route]) => {
      const key = route.meta.titleKey ?? route.meta.menu?.labelKey
      document.title = key ? `${t(key)} · ${t('app.name')}` : t('app.name')
    },
    { immediate: true },
  )

  watch(
    () => auth.user,
    (user) => setObservabilityContext({ workspace: auth.workspace, userId: user?.id }),
  )

  // Unread notifications badge
  let unreadTimer: ReturnType<typeof setInterval> | undefined
  const refreshUnread = () =>
    sessionApi
      .unreadNotifications()
      .then((r) => notifications.setUnread(r.count))
      .catch(() => {})
  watch(
    () => auth.isAuthenticated,
    (on) => {
      clearInterval(unreadTimer)
      if (on) {
        void refreshUnread()
        unreadTimer = setInterval(() => void refreshUnread(), 60_000)
      }
    },
    { immediate: true },
  )
  onScopeDispose(() => clearInterval(unreadTimer))

  // Idle logout (15 minutes by default; confirm with the bank)
  useIdleLogout(
    computed(() => auth.isAuthenticated),
    getConfig().idleTimeoutMinutes,
    () => {
      void auth.logout().then(() => router.replace({ name: 'login', query: { reason: 'idle' } }))
    },
  )
}
